import React, { useContext } from 'react';
import styled from 'styled-components';
import { BlockIdContext, UserContext } from 'venn-components';
import GrowthSimulationSection from './sections/GrowthSimulationSection';
import MetricsSection from './sections/MetricsSection';
import { useRecoilValue } from 'recoil';
import { blockInfoGraphicType, blockSettings } from 'venn-state';
import ScenarioSection from './sections/ScenarioSection';
import { SubjectInputSection } from './sections/subject-input/SubjectInputSection';
import { BenchmarkInputSection } from './sections/subject-input/benchmark/BenchmarkInputSection';
import { DateInputSection } from './sections/date-range-input/DateInputSection';
import { PanelSection } from '../../PanelSection';
import FormatSection from './sections/FormatSection';
import StrategyLevelSection from './sections/StrategyLevelSection';
import TitleSection from './sections/TitleSection';
import MoreSection from './sections/MoreSection';
import PrivateAssetSection from './sections/PrivateAssetSection';
import {
  hasDisabledBenchmark,
  isHoldingsBreakdownBlock,
  isPortfolioBlock,
  isPublicPrivateAssetGrowthBlock,
  isTreeBlock,
  useHasFF,
} from 'venn-utils';
import HoldingsTreeSection from './sections/HoldingsTreeSection';
import HoldingsBlockSection from './sections/HoldingsBlockSection';
import PeerGroupsSection from './sections/PeerGroupsSection';
import { CustomFontSection } from './sections/CustomFontSection';
import { TableSpacingSection } from './sections/TableSpacingSection';
import { ComparisonOrderSection } from './sections/ComparisonOrderSection';
import { PrivateAssetBlockAssignmentSection } from './sections/PrivateAssetBlockAssignmentSection';

/**
 * Input Management component for modifying block settings/config for analysis blocks (e.g. not text, image, etc).
 */
export const AnalysisBlockTab = React.memo(function AnalysisBlockTab({ selected }: { selected: string }) {
  const { hasPermission } = useContext(UserContext);
  const hasHoldingsMetadata = useHasFF('holdings_metadata');
  const hasBlockAssignment = useHasFF('block_assignment_ff');

  const settings = useRecoilValue(blockSettings(selected));

  return (
    <>
      {!hasBlockAssignment && <BlockInputsSection selected={selected} />}
      {hasHoldingsMetadata && isPortfolioBlock(settings.customBlockType) && (
        <ComparisonOrderSection selectedRefId={selected} />
      )}
      {(settings.customBlockType === 'GROWTH_SIMULATION' ||
        isPublicPrivateAssetGrowthBlock(settings.customBlockType)) && <GrowthSimulationSection selected={selected} />}
      {isTreeBlock(settings.customBlockType) && (
        <HoldingsTreeSection selected={[]} blockType={settings.customBlockType} selectedRefId={selected} />
      )}
      {['ASSET_EXPOSURE', 'CREDIT_BREAKDOWN', 'MATURITY_BREAKDOWN', 'DURATION_BREAKDOWN'].includes(
        settings.customBlockType,
      ) && <HoldingsBlockSection selectedRefId={selected} />}

      {hasBlockAssignment ? (
        <BlockIdContext.Provider value={selected}>
          <PrivateAssetBlockAssignmentSection selectedBlockId={selected} />
        </BlockIdContext.Provider>
      ) : (
        <PrivateAssetSection selected={selected} />
      )}
      <ScenarioSection selected={selected} />
      {settings.customBlockType === 'PEER_GROUPS' && <PeerGroupsSection selected={selected} />}
      <FormatSection selected={selected} isReadOnly={!hasPermission('STUDIO_EDIT_FORMAT')} />
      <StrategyLevelSection selected={selected} isReadOnly={!hasPermission('STUDIO_EDIT_FORMAT')} />
      {hasHoldingsMetadata && isHoldingsBreakdownBlock(settings.customBlockType) && (
        <ComparisonOrderSection selectedRefId={selected} />
      )}

      <MetricsSection selected={selected} />
      <TitleSection selected={selected} />
      <MoreSection selected={selected} />
      <TableSpacingSection selected={selected} isReadOnly={!hasPermission('STUDIO_EDIT_FORMAT')} />
      <CustomFontSection selected={selected} isReadOnly={!hasPermission('STUDIO_EDIT_FORMAT')} />
    </>
  );
});

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const BlockInputsSection = React.memo(({ selected }: { selected: string }) => {
  const settings = useRecoilValue(blockSettings(selected));
  const blockGraphicType = useRecoilValue(blockInfoGraphicType(selected));
  return (
    <PanelSection header="Set Inputs on Block">
      <InputSection>
        <SubjectInputSection selectedBlockId={selected} />
        <DateInputSection selectedBlockId={selected} />
        {!hasDisabledBenchmark(settings.customBlockType, blockGraphicType) && (
          <BenchmarkInputSection selectedBlockId={selected} />
        )}
      </InputSection>
    </PanelSection>
  );
});

const InputSection = styled.div`
  display: flex;
  row-gap: 20px;
  flex-direction: column;
`;
